import { useEffect, useRef, useState } from "react";
import { Pencil, Check } from "lucide-react";
import { Control, useFormContext, useWatch } from "react-hook-form";
import { volumesOptions } from "@/constants/options/volumes.option";
import clsx from "clsx";
import TrashIcon from "@/assets/icons/trash.svg?react";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { cn } from "@/lib/utils";

type ProductElemProps = {
  index: number;
  remove: (index: number) => void;
  control: Control<CotationFormData>;
  error: boolean;
};

export const ProductElem = ({ index, remove, control, error }: ProductElemProps) => {
  const methods = useFormContext<CotationFormData>();

  // Watch current form values for this product to get live updates
  const products = useWatch({
    control,
    name: "products",
  });
  const product = useWatch({
    control,
    name: `products.${index}`,
  });

  const { volume, bottleQuantity, value, isNew } = product;

  const [isEditing, setIsEditing] = useState(isNew);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  // When finishing edit, clear the isNew flag
  const handleFinishEditing = async () => {
    const trigger = await methods.trigger(`products.${index}`);
    if (!trigger) return;
    methods.setValue(`products.${index}.isNew`, false);
    setIsEditing(false);
  };

  useEffect(() => {
    setIsEditing(isNew);
  }, [isNew]);

  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        // Check if the focused element is inside our input container
        if (
          inputContainerRef.current &&
          inputContainerRef.current.contains(document.activeElement)
        ) {
          handleFinishEditing();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  if (isEditing) {
    return (
      <div
        ref={inputContainerRef}
        className={clsx("rounded-lg sm:flex", {
          "grid grid-cols-2 gap-2 border-2 border-gray-300 bg-gray-50 p-4": !product?.isNew,
          "flex flex-col gap-2 sm:flex-row": product?.isNew,
          "border-red-500": error,
        })}
      >
        <ControlledSelectV2
          control={control}
          name={`products.${index}.volume`}
          setValue={(e) => {
            methods.setValue(`products.${index}.volume`, Number(e));
          }}
          options={volumesOptions.map((volume) => ({
            label: volume.label,
            value: volume.value.toString(),
          }))}
          label="Volume"
          wrapperClassName="flex-1"
        />
        <ControlledUnifiedInput
          control={control}
          name={`products.${index}.bottleQuantity`}
          inputType="integer"
          label="Nb de bouteilles"
          wrapperClassName="flex-1"
        />
        <ControlledUnifiedInput
          control={control}
          name={`products.${index}.value`}
          inputType="float"
          label="Valeur totale"
          rightElement={<span>€</span>}
          wrapperClassName="flex-1"
        />
        <div className="flex flex-row items-center justify-center gap-2 sm:justify-end">
          <button
            onClick={handleFinishEditing}
            className={clsx("", {
              hidden: product?.isNew,
            })}
          >
            <Check />
          </button>
          <button
            onClick={() => remove(index)}
            className={cn("rounded-full p-2 hover:bg-gray-200", {
              hidden: products.length <= 1,
            })}
          >
            <TrashIcon className="text-red-600" width={20} height={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "grid grid-cols-2 gap-2 rounded-lg border-2 border-gray-300 bg-gray-50 p-4 sm:grid-cols-4",
        {
          "border-red-500": error,
        },
      )}
    >
      <div className="flex flex-1 flex-col">
        <p className="text-xs">Volume</p>
        <p className="font-medium">{volumesOptions?.find((el) => el.value === volume)?.label}</p>
      </div>
      <div className="flex flex-1 flex-col">
        <p className="text-xs">Nb de bouteilles</p>
        <p className="font-medium">{isNaN(bottleQuantity) ? "-" : bottleQuantity}</p>
      </div>
      <div className="flex flex-1 flex-col">
        <p className="text-xs">Valeur totale</p>
        <p className="font-medium">{isNaN(value) ? "-" : value} €</p>
      </div>
      <div className="flex flex-1 flex-row items-center gap-2 sm:justify-end">
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="rounded-full p-2 hover:bg-gray-200"
        >
          <Pencil fill="#000" width={20} height={20} />
        </button>
        <button
          onClick={() => remove(index)}
          className={cn("rounded-full p-2 hover:bg-gray-200", {
            hidden: products.length <= 1,
          })}
        >
          <TrashIcon className="text-red-600" width={20} height={20} />
        </button>
      </div>
    </div>
  );
};
